/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import React, { useCallback, useEffect, useState } from 'react';

import { excelFilesQueryKey } from '@futrli/views/queries/excelFiles';
import { invalidateDriverQueries } from '@futrli/views/mutations/nonFinancialDrivers';
import { queryClient } from '@futrli/views/core/queryClient';

import { ExcelUploadDrawer } from './excel-upload-drawer';
import { UploadEntity } from './upload-entity';
import { useUploadDrivers } from '../hooks/use-upload-drivers';

export interface DriversUploadDrawerInnerProps {
  isLoading?: boolean;
  children: JSX.Element;
  handleClose: () => void;
  maxFileSize?: number; // in MB
  acceptedFileExtensions?: string;
  workspaceId: string;
  organisationId: string;
  onSuccess: () => Promise<void>;
  isUpdateDrawer: boolean;
  width?: number;
}

export const DriversUploadDrawerInner = ({
  children,
  handleClose,
  workspaceId,
  organisationId,
  maxFileSize = 25,
  acceptedFileExtensions = '.xls,.xlsx',
  isLoading,
  onSuccess,
  isUpdateDrawer,
  width
}: DriversUploadDrawerInnerProps) => {
  const [file, setFile] = useState<File>();

  const {
    uploadFailed,
    uploadSuccess,
    setUploadSuccess,
    isInProgress,
    step,
    setStep,
    errorMessage,
    setErrorMessage,
    warningMessage,
    setWarningMessage,
    createDriverFromExcelFile
  } = useUploadDrivers(workspaceId, organisationId);

  useEffect(() => {
    if (uploadSuccess) {
      queryClient.invalidateQueries({
        queryKey: excelFilesQueryKey(workspaceId, organisationId, { file_type: 'driver' })
      });
      invalidateDriverQueries(workspaceId, organisationId);
    }
  }, [uploadSuccess, workspaceId, organisationId]);

  useEffect(() => {
    if (file && !isInProgress && step === 'ready') {
      createDriverFromExcelFile(file);
    }
  }, [file, isInProgress, workspaceId, step, createDriverFromExcelFile]);

  const onFileChange = useCallback((file: File) => {
    setFile(file);
  }, []);

  useEffect(() => {
    if (uploadFailed) {
      setFile(undefined);
      setStep('ready');
    }
  }, [uploadFailed, setStep]);

  useEffect(() => {
    if (uploadSuccess) {
      setFile(undefined);
      setStep('ready');
    }
  }, [uploadSuccess, setStep]);

  const onCancel = useCallback(() => {
    setFile(undefined);
    setErrorMessage('');
    setWarningMessage('');
    setStep('ready');
    setUploadSuccess(false);
    handleClose();
  }, [handleClose, setErrorMessage, setStep, setUploadSuccess, setWarningMessage]);

  const onFinish = useCallback(async () => {
    onSuccess();
  }, [onSuccess]);

  return (
    <ExcelUploadDrawer
      maxFileSize={maxFileSize}
      acceptedFileExtensions={acceptedFileExtensions}
      onCancel={onCancel}
      isInProgress={isInProgress}
      uploadSuccess={uploadSuccess}
      uploadFailed={uploadFailed}
      file={file}
      errorMessage={errorMessage}
      warningMessage={warningMessage}
      step={step}
      entity={UploadEntity.Driver}
      onFileChange={onFileChange}
      onFinish={onFinish}
      isUpdateDrawer={isUpdateDrawer}
      isLoading={isLoading}
      workspaceId={workspaceId}
      organisationId={organisationId}
      historyConfig={{ entityType: 'driver' }}
      width={width}
    >
      {children}
    </ExcelUploadDrawer>
  );
};
